/** @format */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'src/utils/axios';
import { ENDPOINTS, ACTION_CONSTANTS } from 'src/constants';
import { saveDashboard } from 'src/slices/dashboards';
import i18n from 'src/i18n';

const USER_PROFILE_API_URL = process.env.REACT_APP_USER_PROFILE_API_URL;

const initialState = {
  userLoading: false,
  dashboardLoading: false,
  ticketDetail: null,
  tickets: null,
  dashboardInfo: null,
  currentUser: null,
  widgetInfo: null,
  error: null,
  dashboardError: null,
  userProfile: {
    picture: null,
    pictureLoading: false
  },
  userData: null,
  currentUserDashboard: null,
  onBoardingComplete: false
};

const slice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    //Get User

    getUser(state) {
      state.userLoading = true;
    },
    getUserSuccess(state, action) {
      const user = action.payload?.user ?? action.payload?.data ?? action.payload;
      state.currentUser = user;
      state.userLoading = false;
    },
    getUserFailure(state, action) {
      const { error } = action.payload;
      state.error = error;
      state.userLoading = false;
    },

    //Get  widget info
    getWidgetInfo(state) {
      state.userLoading = true;
    },
    getWidgetInfoSuccess(state, action) {
      const { data, lang } = action.payload;
      state.widgetInfo = {
        ...data,
        data: data.data?.filter(item => {
          if (['en', 'de', 'en-GB'].includes(lang)) {
            return item.tag !== 'tis-web-orders' && item.tag !== 'tis-web-invoices';
          }
          return true;
        })
      };
      state.userLoading = false;
    },
    getWidgetInfoFailure(state, action) {
      // const { error } = action.payload;
      state.error = action.payload;
      state.userLoading = false;
    },
    // Dashboard Info
    setDashboardInfo(state) {
      state.userLoading = true;
    },
    setDashboardInfoSuccess(state, action) {
      const { data } = action.payload;
      state.dashboardInfo = data;
      state.userLoading = false;
    },
    setDashboardInfoFailure(state, action) {
      state.error = action.payload;
      state.userLoading = false;
    },
    // Update Users
    updateUser(state) {
      state.userLoading = true;
    },
    updateUserSuccess(state, action) {
      // const { user } = action.payload;
      // state.currentUser = user;
      state.userLoading = false;
    },
    updateUserFailure(state, action) {
      const { error } = action.payload;
      state.error = error;
      state.userLoading = false;
    },
    // UserProfile
    getUserProfilePicture(state, action) {
      state.userProfile.pictureLoading = true;
    },
    setUserProfilePicture(state, action) {
      const { picture } = action.payload;
      if (picture) {
        state.userProfile.picture = picture;
      }
      state.userProfile.pictureLoading = false;
      state.userLoading = false;
      state.error = null;
    },
    getUserProfilePictureFailure(state, action) {
      state.userProfile.pictureLoading = false;
      state.userLoading = false;
    },
    updateUserProfilePictureSuccess(state, action) {
      state.userLoading = false;
      state.error = null;
    },
    deleteUserProfilePictureSuccess(state, action) {
      state.userLoading = false;
      state.error = null;
      state.userProfile.picture = null;
    },
    updateUserProfilePictureFailure(state, action) {
      state.error = action.payload;
      state.userLoading = false;
      state.userProfile.pictureLoading = false;
    },
    setUserData(state, action) {
      state.userData = action.payload;
    },
    getDashboard(state, action) {
      state.dashboardLoading = true;
      state.dashboardError = null;
    },
    getDashboardSuccess(state, action) {
      const { data } = action.payload;
      state.dashboardLoading = false;
      state.dashboardInfo = data;
    },
    getDashboardFailure(state, action) {
      state.dashboardLoading = false;
      state.dashboardError = true;
      state.dashboardInfo = null;
    },
    setCurrentUserDashboard(state, action) {
      const { dashboardId } = action.payload;
      state.currentUserDashboard = dashboardId;
    },
    setOnBoardingCompleteSuccess(state) {
      state.onBoardingComplete = true;
    }
  },
  extraReducers: builder => {
    builder.addCase(onBoardingComplete.fulfilled, state => {
      state.onBoardingComplete = true;
    });
  }
});

export const reducer = slice.reducer;

export const getUser = () => async dispatch => {
  const { getUser, getUserSuccess, getUserFailure } = slice.actions;

  try {
    dispatch(getUser());
    const response = await axios.get(ENDPOINTS.GET_USER_INFO_ENDPOINT);
    const user = response.data;
    dispatch(getUserSuccess({ user }));
  } catch (err) {
    dispatch(getUserFailure(err));
  }
};

export const setUserData = data => async dispatch => {
  const { setUserData } = slice.actions;
  dispatch(setUserData(data));
};

export const getWidgetInfo = () => async dispatch => {
  const { getWidgetInfo, getWidgetInfoSuccess, getWidgetInfoFailure } = slice.actions;
  const lang = i18n.language || 'en';
  try {
    dispatch(getWidgetInfo());
    const response = await axios.get(ENDPOINTS.GET_WIDGET_ENDPOINT);

    //  localStorage.setItem('layouts', JSON.stringify(response.data.dashboardConfig)); // save to localstage
    dispatch(getWidgetInfoSuccess({ ...response, lang }));
  } catch (err) {
    dispatch(getWidgetInfoFailure(err));
  }
};

export const setDashboardInfo = (layouts, id) => async dispatch => {
  const { setDashboardInfo, setDashboardInfoSuccess, setDashboardInfoFailure } = slice.actions;
  try {
    dispatch(setDashboardInfo());
    if (id) {
      const response = await axios.patch(`${ENDPOINTS.SET_DASHBOARD_LAYOUT}/${id}`, layouts);
      // console.log(response);
      //  localStorage.setItem('layouts', JSON.stringify(response.data.dashboardConfig)); // save to localstage
      dispatch(setDashboardInfoSuccess(response));
    }
  } catch (err) {
    dispatch(setDashboardInfoFailure(err));
  }
};

export const updateUserInfo = payload => async dispatch => {
  const { updateUser, updateUserSuccess, updateUserFailure, setUserData } = slice.actions;

  try {
    dispatch(updateUser());
    const response = await axios.put(ENDPOINTS.GET_USER_INFO_ENDPOINT, payload);
    dispatch(updateUserSuccess());
    dispatch(setUserData(payload));
    return response;
  } catch (err) {
    dispatch(updateUserFailure(err));
  }
};

export const changePassword = payload => async dispatch => {
  const { updateUser, updateUserFailure } = slice.actions;
  // const { uuid } = payload;
  try {
    dispatch(updateUser());
    const response = await axios.put(`${ENDPOINTS.GET_USER_ENDPOINT}/password`, payload);
    return response;
  } catch (err) {
    dispatch(updateUserFailure(err));
  }
};

// User Profile Picture
export const updateUserProfilePicture = () => async dispatch => {
  const { getUserProfilePicture, setUserProfilePicture, getUserProfilePictureFailure } = slice.actions;
  try {
    dispatch(getUserProfilePicture());
    const response = await axios.get(`${USER_PROFILE_API_URL}api/v1/user/picture`, { responseType: 'arraybuffer' });
    if (response && response.data) {
      const picture = window.URL.createObjectURL(new Blob([response.data]));
      dispatch(setUserProfilePicture({ picture }));
    }
  } catch (err) {
    dispatch(getUserProfilePictureFailure());
  }
};

export const uploadUserProfilePicture = payload => async dispatch => {
  const { updateUser, updateUserProfilePictureFailure, updateUserProfilePictureSuccess } = slice.actions;
  const { data } = payload;
  try {
    dispatch(updateUser());
    const response = await axios.post(`${USER_PROFILE_API_URL}api/v1/user/picture`, data, {
      headers: {
        'Content-Type': 'image/png'
      }
    });
    if (!response.error) dispatch(updateUserProfilePictureSuccess());
  } catch (err) {
    dispatch(updateUserProfilePictureFailure({ error: err.message }));
  }
};

export const deleteUserProfilePicture = payload => async dispatch => {
  const { updateUser, deleteUserProfilePictureSuccess } = slice.actions;
  try {
    dispatch(updateUser());
    const response = await axios.delete(`${USER_PROFILE_API_URL}api/v1/user/picture`);
    dispatch(deleteUserProfilePictureSuccess());
    return response;
  } catch (err) {
    // console.log(err);
  }
};

// User dashboard
export const getDashboard =
  ({ dashboardId }) =>
  async dispatch => {
    const { getDashboard, getDashboardSuccess, getDashboardFailure } = slice.actions;
    try {
      dispatch(getDashboard());
      const response = await axios.get(`${ENDPOINTS.GET_DASHBOARD_ENDPOINT}${dashboardId}`);

      const { data } = response;
      dispatch(getDashboardSuccess({ data }));
      dispatch(saveDashboard({ dashboardId, data }));
    } catch (err) {
      dispatch(getDashboardFailure(err));
    }
  };

export const setCurrentUserDashboard = dashboardId => async dispatch => {
  const { setCurrentUserDashboard } = slice.actions;
  dispatch(setCurrentUserDashboard({ dashboardId }));
};

export const onBoardingComplete = createAsyncThunk(ACTION_CONSTANTS.ON_BOARDING_COMPLETE, async payload => {
  const { uuid } = payload;
  const response = await axios.post(`${ENDPOINTS.ONBOARDING_COMPLETE_ENDPOINT}/${uuid}/onboarding-complete`);
  return response.status;
});

export const completeOnBoarding = uuid => async dispatch => {
  try {
    await dispatch(onBoardingComplete({ uuid })).unwrap();
    return true;
  } catch (err) {
    return false;
  }
};

export default slice;
